import { useCallback, useEffect, useMemo } from 'react';

import { MARKET_FILTERS } from '@/constants/market';
import { useAppDispatch } from '@/hooks';
import {
  ALL_SELECTOR,
  CUSTOM_SELECTOR,
  FAV_SELECTOR,
  // ALL_SELECTOR,
  MARGIN_SELECTORS,
  // MARGIN_TYPE,
  PAIR_DATE_TYPE,
  TPairTypeForMobile,
} from '@/types/global';
import { IMarginSearchProps } from '@/types/search';

import { CHAIN_ID, getAllDisplayableChainMarketCustomPairs } from '@/constants/chain';
import { TVL_THRESHHOLD } from '@/constants/global';
import { QUERY_KEYS } from '@/constants/query';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { IMarketPair } from '@/features/market/types';
import { queryClient } from '@/global';
import { useFavMarketPairs } from '@/hooks/useFavorites';
import useSocket from '@/hooks/useSocket';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';

import { SOCKET_EVENTS, SOCKET_METHODS, SOCKET_TYPES } from '@/entities/SocketService';
import { getChainIdByChainShortName } from '@/utils/chain';
import { getSavedPairChosen } from '@/utils/localstorage';
import { PERP_EXPIRY } from '@synfutures/sdks-perp';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useBackendChainConfig, useConfigData } from '../config/hook';
import { useCurrentMarketType } from '../global/hooks';
import { transformMarketInfo } from './api';
import { useFetchMarketPairList } from './query';
import { useMarketPairInfoStore } from './store';

export function useMarginSearch(): {
  marginSearchProps: IMarginSearchProps;
  goToSelectorWithFilter: (
    marginSelector: MARGIN_SELECTORS,
    marginCustomSelectorId?: string,
    filter?: MARKET_FILTERS,
    search?: string,
    selectedChainIds?: number[],
  ) => void;
  resetMarginSearch: (filter?: MARKET_FILTERS) => void;
  pairTypeForMobile: TPairTypeForMobile;
} {
  const { marginSearchProps, setMarginSearchProps } = useMarketPairInfoStore();
  const goToSelectorWithFilter = useCallback(
    (
      marginSelector: MARGIN_SELECTORS,
      marginCustomSelectorId?: string,
      filter = MARKET_FILTERS.PERPETUAL,
      search?: string,
      selectedChainIds?: number[],
    ) => {
      setMarginSearchProps({
        marginSelector: marginSelector || marginSearchProps.marginSelector,
        filter: filter || marginSearchProps.filter || MARKET_FILTERS.ALL,
        marginCustomSelectorId: marginCustomSelectorId || marginSearchProps.marginCustomSelectorId || null,
        search,
        selectedChainIds: selectedChainIds !== undefined ? selectedChainIds : marginSearchProps.selectedChainIds,
      });
    },
    [
      setMarginSearchProps,
      marginSearchProps.marginSelector,
      marginSearchProps.filter,
      marginSearchProps.marginCustomSelectorId,
      marginSearchProps.selectedChainIds,
    ],
  );
  const resetMarginSearch = useCallback(
    (filter = MARKET_FILTERS.PERPETUAL) => {
      setMarginSearchProps({
        // marginSelector: undefined,
        filter,
        marginSelector: ALL_SELECTOR.ALL,
        marginCustomSelectorId: null,
      });
    },
    [setMarginSearchProps],
  );
  const pairTypeForMobile: TPairTypeForMobile = useMemo(() => {
    const { filter } = marginSearchProps;
    if (filter == MARKET_FILTERS.DATED) {
      return PAIR_DATE_TYPE.DATED;
    }
    if (filter == MARKET_FILTERS.PERPETUAL) {
      return PAIR_DATE_TYPE.PERPETUAL;
    }
    // if (marginSelector === ALL_SELECTOR.ALL) return undefined;
    // if (marginSelector === MARGIN_TYPE.COIN || marginSelector === MARGIN_TYPE.USD) {
    //   return marginSelector as TPairTypeForMobile;
    // }
    return undefined;
  }, [marginSearchProps]);

  return {
    goToSelectorWithFilter,
    marginSearchProps,
    resetMarginSearch,
    pairTypeForMobile,
  };
}

export function useMarketList(chainId: CHAIN_ID | undefined, exclude?: boolean) {
  const { data: marketChainPairList, isFetched } = useFetchMarketPairList({ chainId });
  const { backendChainsConfig } = useConfigData();
  const pairsToBeExcluded: string[] = useMemo(
    () => _.get(backendChainsConfig, [chainId || ''])?.pairsToBeFilteredInMarketAndEarn || [],
    [backendChainsConfig, chainId],
  );
  const marketPairList = useMemo(() => {
    return _.flatMap(
      marketChainPairList,
      // ?.filter((item) => SUPPORTED_CHAIN_ID.includes(Number(item.chainId)))
      // ?.filter((item) => (!!chainId?.length ? chainId.includes(item.chainId) : true))
      // .map((v) => v.pairs)
    )?.filter((item) => (exclude ? !pairsToBeExcluded?.includes(item.symbol) : true));
  }, [marketChainPairList, pairsToBeExcluded, exclude]);
  return { marketPairList, marketChainPairList, isFetched };
}

export function useFilteredTradingPairs(): { filteredPairs: IMarketPair[] | undefined; showMargin?: boolean } {
  const {
    marginSearchProps: {
      marginSelector,
      filter,
      marginCustomSelectorId,
      search,
      // selectedChainIds
    },
  } = useMarginSearch();
  const chainId = useChainId();

  const { backendChainsConfig } = useConfigData();
  const { marketPairList } = useMarketList(chainId);
  const favPairs = useFavMarketPairs(chainId);
  const marginPairs = useMemo(() => {
    if (marginSelector === CUSTOM_SELECTOR.CUSTOM) {
      return marketPairList?.filter((pair) =>
        getAllDisplayableChainMarketCustomPairs(backendChainsConfig)
          .find((item) => item.id === marginCustomSelectorId && Number(item.chainId) === Number(pair.chainId))
          ?.pairs.includes(pair.symbol),
      );
    }

    // return marketPairList?.filter((pair) => {
    //   {
    //     if (!marginSelector) return true;
    //     return getMarginTypeByFeeder(pair.feederType) === marginSelector;
    //   }
    // });

    return marketPairList;
  }, [marginSelector, marketPairList, backendChainsConfig, marginCustomSelectorId]);
  const pairsToBeFiltered = useMemo(
    () => _.flatMap(backendChainsConfig[chainId || '']?.pairsToBeFilteredInMarketAndEarn),
    [backendChainsConfig, chainId],
  );
  const filteredPairs = useMemo(() => {
    if (marginSelector === FAV_SELECTOR.FAVORITE) return favPairs;
    const requiredPairs = marginSelector === ALL_SELECTOR.ALL ? marketPairList : marginPairs;
    return requiredPairs
      ?.filter(
        (p) =>
          !search ||
          p.baseToken?.symbol.toLowerCase().includes(search.toLowerCase()) ||
          p.quoteToken?.symbol.toLowerCase().includes(search.toLowerCase()),
      )
      .filter((p) => !pairsToBeFiltered?.includes(p.symbol))
      .filter((pair) => {
        switch (filter) {
          case MARKET_FILTERS.PERPETUAL:
            return pair.expiry === PERP_EXPIRY;
          case MARKET_FILTERS.DATED:
            return pair.expiry !== PERP_EXPIRY;
          default:
            return true;
        }
      })
      .filter((p) => WrappedBigNumber.from(p.tvlUsd).gte(TVL_THRESHHOLD));
  }, [marginSelector, favPairs, marketPairList, marginPairs, search, pairsToBeFiltered, filter]);

  return { filteredPairs, showMargin: false };
}

export function useCurrentMarketPairFromUrl(chainId: number | undefined): IMarketPair | undefined {
  const { pairSymbol, chainShortName } = useParams();
  const chainIdFromUrl = useMemo(() => {
    return chainShortName ? getChainIdByChainShortName(chainShortName) : undefined;
  }, [chainShortName]);
  const symbolInStorage = useDefaultPairSymbolFromUrlOrFallbackLocalOrApi(chainId);
  const { marketPairList } = useMarketList(chainId);
  const pair = useMemo(() => {
    if (chainIdFromUrl !== chainId) return undefined;
    const symbol = pairSymbol || symbolInStorage;
    return [...(marketPairList || [])].find((wp) => wp.symbol === symbol);
  }, [chainId, chainIdFromUrl, marketPairList, pairSymbol, symbolInStorage]);
  return pair;
}

export function useDefaultPairSymbolFromUrlOrFallbackLocalOrApi(chainId: number | undefined): string | undefined {
  const userAddr = useUserAddr();
  const marketType = useCurrentMarketType();
  const { marketPairList } = useMarketList(chainId);
  const symbolInStorage = marketType ? getSavedPairChosen(userAddr, chainId, marketType) : undefined;
  const dappChainConfig = useBackendChainConfig(chainId);
  return useMemo(() => {
    const symbol =
      symbolInStorage ||
      dappChainConfig?.trade?.defaultPairSymbol ||
      (marketPairList?.length ? marketPairList[0].symbol : undefined);
    return symbol;
  }, [dappChainConfig?.trade?.defaultPairSymbol, marketPairList, symbolInStorage]);
}

export function useWatchMarketListChange(chainId: number | undefined): void {
  const { socket } = useSocket();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!socket || !chainId) return;
    const param = {
      chainId: chainId,
      type: SOCKET_TYPES.COMMON,
    };
    const subscribeId = Date.now();
    socket.emit({ id: subscribeId, method: SOCKET_METHODS.SUBSCRIBE, params: param });

    socket.on(SOCKET_EVENTS.MARKET_LIST_CHANGED, (data) => {
      console.record('event', SOCKET_EVENTS.MARKET_LIST_CHANGED, data);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newData = data.map((p: any) => {
        return transformMarketInfo(p, chainId);
      });

      queryClient.setQueryData(QUERY_KEYS.MARKET.PAIR_LIST(chainId), newData);
    });

    return () => {
      socket.off(SOCKET_EVENTS.MARKET_LIST_CHANGED);
      // socket.emit({ id: Date.now(), method: SOCKET_METHODS.UNSUBSCRIBE, params: param });
    };
  }, [chainId, dispatch, socket]);
}
